/**
 * The scheduling-bench table (/planning via SchedulingBench) plus the
 * pipeline mutation handlers shared with /intake's ApprovalQueue.
 *
 * Until 2026-08-14 this table also rendered /intake's approval queue
 * via a mode prop; the intake table is now its own lean, read-only
 * component (components/intake/ApprovalQueue.tsx) — editors like size,
 * dates, forecast exclude, and role slots belong to the bench only.
 * The forecast heatmap/Gantt still cover the WHOLE dated pre-start
 * pipeline (requested included): inclusion in the forecast is driven by
 * having a date window, never by approval status, so nobody has to
 * fake-approve a project to model it. Nothing here writes to the live
 * heatmap or capacity engine — the forecast only READS committed demand
 * and overlays the pipeline on top.
 */
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronRight, Plus, Trash2, Users, Check, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { useUpdateProject } from "@/hooks/usePortfolio";
import {
  useProjectAssignments,
  useCreateAssignment,
  useDeleteAssignment,
} from "@/hooks/useAssignments";
import { shortDate } from "@/lib/format";
import { statusLabel } from "@/lib/projectStatus";
import type { Project } from "@/types/project";
import { cn } from "@/lib/cn";
import { toast } from "sonner";

// Roles the picker offers — the canonical set the heatmap renders.
const ROLE_OPTIONS: { key: string; label: string }[] = [
  { key: "pm", label: "PM" },
  { key: "ba", label: "BA" },
  { key: "functional", label: "Functional" },
  { key: "technical", label: "Technical" },
  { key: "developer", label: "Developer" },
  { key: "infrastructure", label: "Infrastructure" },
  { key: "dba", label: "DBA" },
  { key: "erp", label: "ERP" },
];
const ROLE_LABEL: Record<string, string> = Object.fromEntries(
  ROLE_OPTIONS.map((r) => [r.key, r.label]),
);

// The org's real t-shirt labels (from Jira). Kept as the exact strings
// so the backend's midpoint sizing parses them consistently.
const TSHIRT_OPTIONS = [
  "S: < 80 Hours",
  "M: 80-160 Hours",
  "L: 160-320 Hours",
  "XL: 320-640 Hours",
  "XXL: > 640 Hours",
];

export function isDated(p: Project): boolean {
  // A pipeline project lands on the forecast once it has a date window;
  // its per-role demand then comes from the resource types you add.
  return Boolean(p.start_date && p.end_date);
}

/** Shared mutation handlers over the pipeline forecast query. */
export function usePipelineActions() {
  const update = useUpdateProject();

  const patch = (id: string, field: string, value: string | null) => {
    update.mutate(
      { id, [field]: value || null },
      { onError: (e) => toast.error((e as Error).message || "Couldn't save") },
    );
  };

  const setExcluded = (id: string, excluded: boolean) => {
    update.mutate(
      { id, exclude_from_pipeline: excluded },
      { onError: (e) => toast.error((e as Error).message || "Couldn't save") },
    );
  };

  // Start work: flip the project to an active status. That single change
  // moves it off the bench AND onto the live capacity engine (both are
  // driven by status), so it can't fall into a gap.
  const startWork = (id: string, status: string) => {
    update.mutate(
      { id, status },
      {
        onSuccess: () =>
          toast.success("Work started — now on the live capacity plan"),
        onError: (e) => toast.error((e as Error).message || "Couldn't start work"),
      },
    );
  };

  // Approve: the CFO gate. Requested -> approved moves it to the
  // bottom of Next up on the Top Projects board (connector rule);
  // the lifecycle card stamps the
  // approval date.
  const approve = (id: string) => {
    update.mutate(
      { id, status: "approved" },
      {
        onSuccess: () =>
          toast.success("Approved — added to Next up on Top Projects"),
        onError: (e) => toast.error((e as Error).message || "Couldn't approve"),
      },
    );
  };

  // Agenda: PMO schedules a requested project for the monthly CFO
  // decision meeting. Cleared automatically when a decision is made
  // (status leaves 'requested').
  const setAgenda = (id: string, on: boolean) => {
    update.mutate(
      { id, cfo_agenda: on },
      {
        onSuccess: () =>
          toast.success(on ? "Added to the approval meeting agenda" : "Removed from the agenda"),
        onError: (e) => toast.error((e as Error).message || "Couldn't update agenda"),
      },
    );
  };

  // Decline: set the request to the terminal 'declined' status. It drops
  // off the queue but the record is kept (reversible from the project page).
  const decline = (id: string) => {
    update.mutate(
      { id, status: "declined" },
      {
        onSuccess: () =>
          toast.success("Request declined — removed from the queue, record kept"),
        onError: (e) => toast.error((e as Error).message || "Couldn't decline"),
      },
    );
  };

  return { update, patch, setExcluded, startWork, approve, setAgenda, decline };
}

// ---------------------------------------------------------------------------
// Sorting — clickable column headers. Ranks put the "interesting" group first
// on an ascending sort (e.g. Kickstart received at the top).
// ---------------------------------------------------------------------------
type SortKey = "name" | "status" | "sponsor" | "received" | "kickstart" | "tshirt" | "start" | "end" | "forecast";
type SortState = { key: SortKey; dir: "asc" | "desc" };

function kickstartRank(status?: string | null): number {
  const s = (status ?? "").toLowerCase();
  if (s === "submitted" || s === "approved") return 0; // Received
  if (s === "not_required") return 2; // N/A
  return 1; // Not received
}

function tshirtRank(size?: string | null): number {
  const i = TSHIRT_OPTIONS.indexOf(size ?? "");
  return i === -1 ? 99 : i;
}

function forecastRank(p: Project): number {
  if (p.exclude_from_pipeline) return 2;
  return isDated(p) ? 0 : 1; // In forecast, then Needs dates, then Excluded
}

function sortValue(p: Project, key: SortKey): string | number {
  switch (key) {
    case "name": return p.name.toLowerCase();
    case "status": return statusLabel(p.status).toLowerCase();
    case "sponsor": return ((p.effective_sponsor ?? p.sponsor) || "~").toLowerCase();
    case "received": return p.requested_at ?? "9999-99-99"; // undated last
    case "kickstart": return kickstartRank(p.brief_status);
    case "tshirt": return tshirtRank(p.tshirt_size);
    case "start": return p.start_date ?? "9999-99-99";
    case "end": return p.end_date ?? "9999-99-99";
    case "forecast": return forecastRank(p);
  }
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState | null;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sort?.key === sortKey;
  return (
    <th className={cn("px-3 py-2 font-semibold", className)}>
      <button
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-ink-3"
      >
        {label}
        {active ? (
          sort!.dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 text-ink-faint" />
        )}
      </button>
    </th>
  );
}

export function PipelineTable({
  projects,
  onPatch,
  onExclude,
  saving,
  onStartWork,
  onDecline,
}: {
  projects: Project[];
  onPatch: (id: string, field: string, value: string | null) => void;
  onExclude: (id: string, excluded: boolean) => void;
  onStartWork: (id: string, status: string) => void;
  onDecline: (id: string) => void;
  saving?: string;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // The bench keeps insertion order until a header is clicked.
  const [sort, setSort] = useState<SortState | null>(null);
  const onSort = (key: SortKey) =>
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  const sorted = useMemo(() => {
    if (!sort) return projects;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...projects].sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [projects, sort]);
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const inputCls =
    "rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink-2 focus:border-line-strong focus:outline-none";
  return (
    <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
      <div className="border-b border-line-soft px-5 py-3">
        <h2 className="text-sm font-semibold text-ink">
          Scheduling bench{" "}
          <span className="text-ink-subtle">({projects.length})</span>
        </h2>
        <p className="text-[11px] text-ink-muted">
          Approved, not yet started. Set gut-feel dates, expand for resource
          types (role + hours/week), then Start work when the team actually
          picks it up.
        </p>
      </div>
      {projects.length === 0 ? (
        <div className="px-5 py-8 text-center text-xs text-ink-subtle">
          Nothing on the bench — every approved project has been started.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-soft text-left text-[10px] uppercase tracking-wider text-ink-subtle">
              <th className="w-6 px-2 py-2"></th>
              <SortHeader label="Project" sortKey="name" sort={sort} onSort={onSort} />
              <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
              <SortHeader label="Sponsor" sortKey="sponsor" sort={sort} onSort={onSort} />
              <SortHeader label="Received" sortKey="received" sort={sort} onSort={onSort} />
              <SortHeader label="Kickstart" sortKey="kickstart" sort={sort} onSort={onSort} />
              <SortHeader label="T-shirt" sortKey="tshirt" sort={sort} onSort={onSort} />
              <SortHeader label="Start" sortKey="start" sort={sort} onSort={onSort} />
              <SortHeader label="End" sortKey="end" sort={sort} onSort={onSort} />
              <SortHeader label="In forecast" sortKey="forecast" sort={sort} onSort={onSort} />
              <th className="px-3 py-2 font-semibold">Move</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {sorted.map((p) => {
              const dated = isDated(p);
              const open = expanded.has(p.id);
              const excluded = Boolean(p.exclude_from_pipeline);
              return (
                <Fragment key={p.id}>
                  <tr className={cn("hover:bg-raised/60", excluded && "opacity-50")}>
                    <td className="px-2 py-2 align-top">
                      <button
                        onClick={() => toggle(p.id)}
                        className="rounded p-0.5 text-ink-subtle hover:bg-raised hover:text-ink-3"
                        title="Resource types"
                      >
                        <ChevronRight className={cn("h-4 w-4 transition-transform", open && "rotate-90")} />
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/portfolio/${p.id}`}
                        className="font-medium text-ink hover:text-brand-navy-700 dark:hover:text-brand-navy-300 hover:underline underline-offset-2"
                        title="Open project detail"
                      >
                        {p.name}
                      </Link>
                      <div className="flex items-center gap-2 font-mono text-[10px] text-ink-subtle">
                        {p.id}
                        {p.source === "freshservice" && p.external_id && (
                          <span className="rounded bg-sky-50 dark:bg-sky-500/10 px-1 py-0.5 font-sans text-[9px] font-semibold text-sky-700 dark:text-sky-300">
                            FS #{p.external_id}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      {statusLabel(p.status)}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      {(p.effective_sponsor ?? p.sponsor) || "—"}
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums text-ink-muted">
                      {p.requested_at ? shortDate(p.requested_at) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <KickstartBadge status={p.brief_status} />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className={inputCls}
                        value={p.tshirt_size ?? ""}
                        onChange={(e) => onPatch(p.id, "tshirt_size", e.target.value)}
                      >
                        <option value="">—</option>
                        {TSHIRT_OPTIONS.map((t) => (
                          <option key={t} value={t}>{t.split(":")[0]}</option>
                        ))}
                        {p.tshirt_size && !TSHIRT_OPTIONS.includes(p.tshirt_size) && (
                          <option value={p.tshirt_size}>{p.tshirt_size}</option>
                        )}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        className={inputCls}
                        value={p.start_date ?? ""}
                        onChange={(e) => onPatch(p.id, "start_date", e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        className={inputCls}
                        value={p.end_date ?? ""}
                        onChange={(e) => onPatch(p.id, "end_date", e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {saving === p.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-subtle" />
                      ) : excluded ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-raised px-2 py-0.5 text-[10px] font-semibold text-ink-muted">
                            Excluded
                          </span>
                          <button
                            onClick={() => onExclude(p.id, false)}
                            className="text-[10px] font-medium text-brand-navy-600 dark:text-brand-navy-400 hover:underline"
                          >
                            Include
                          </button>
                        </div>
                      ) : dated ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                            In forecast
                          </span>
                          <button
                            onClick={() => onExclude(p.id, true)}
                            className="text-[10px] font-medium text-ink-subtle hover:text-ink-3 hover:underline"
                            title="Remove this project from the pipeline forecast"
                          >
                            Exclude
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-ink-subtle">Needs dates</span>
                          <button
                            onClick={() => onExclude(p.id, true)}
                            className="text-[10px] font-medium text-ink-subtle hover:text-ink-3 hover:underline"
                            title="Remove this project from the pipeline forecast"
                          >
                            Exclude
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-start gap-1">
                        <StartWorkPicker onStart={(status) => onStartWork(p.id, status)} />
                        <DeclineButton onDecline={() => onDecline(p.id)} />
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td />
                      <td colSpan={10} className="px-3 pb-3">
                        <RoleSlotsEditor projectId={p.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Kickstart-brief indicator: "received" once the brief is submitted or
// approved; otherwise it's still outstanding (or not required).
function KickstartBadge({ status }: { status?: string | null }) {
  const s = (status ?? "").toLowerCase();
  if (s === "not_required") {
    return <span className="text-[10px] text-ink-subtle">N/A</span>;
  }
  if (s === "submitted" || s === "approved") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
        <Check className="h-3 w-3" /> Received
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
      Not received
    </span>
  );
}

// Start work: picking a phase sets the project's status, which drops it
// off the bench and onto the live capacity engine. Active phases only
// (pre-start statuses would keep it benched, terminal ones would archive
// it). Renamed from "Promote" — that word was doing double duty with the
// service-desk ticket→request promotion on /helpdesk.
const START_PHASES: { value: string; label: string }[] = [
  { value: "functional_spec", label: "Functional Spec" },
  { value: "technical_spec", label: "Technical Spec" },
  { value: "in_progress", label: "In Progress" },
  { value: "testing_go_live", label: "Testing / Go Live" },
];

function StartWorkPicker({ onStart }: { onStart: (status: string) => void }) {
  return (
    <select
      className="rounded-md border border-line bg-surface px-2 py-1 text-[11px] text-ink-3 focus:border-line-strong focus:outline-none"
      value=""
      onChange={(e) => e.target.value && onStart(e.target.value)}
      title="Move this project to active — sets its status and takes it off the bench"
    >
      <option value="">Start work…</option>
      {START_PHASES.map((ph) => (
        <option key={ph.value} value={ph.value}>
          {ph.label}
        </option>
      ))}
    </select>
  );
}

// Decline: two-step so a stray click can't terminate a request. Sets the
// project to 'declined' (terminal) — off intake, but the record is kept and
// it can be reversed from the project page.
function DeclineButton({ onDecline }: { onDecline: () => void }) {
  const [confirming, setConfirming] = useState(false);
  if (confirming) {
    return (
      <span className="inline-flex items-center gap-2 text-[11px]">
        <button
          onClick={() => {
            onDecline();
            setConfirming(false);
          }}
          className="font-semibold text-red-600 dark:text-red-400 hover:underline"
        >
          Decline?
        </button>
        <button
          onClick={() => setConfirming(false)}
          className="text-ink-subtle hover:text-ink-3 hover:underline"
        >
          Cancel
        </button>
      </span>
    );
  }
  return (
    <button
      onClick={() => setConfirming(true)}
      className="text-[11px] font-medium text-ink-subtle hover:text-red-600 dark:hover:text-red-400 hover:underline"
      title="Decline this request — removes it from intake but keeps the record"
    >
      Decline
    </button>
  );
}

// ---------------------------------------------------------------------------
// Resource types (role slots) — same "role + hours/week" mechanism as live
// capacity assignments, but with a role type instead of a named person.
// ---------------------------------------------------------------------------
function RoleSlotsEditor({ projectId }: { projectId: string }) {
  const { data: slots = [], isLoading } = useProjectAssignments(projectId);
  const create = useCreateAssignment();
  const del = useDeleteAssignment();
  const [newRole, setNewRole] = useState("");
  const [newHours, setNewHours] = useState("");
  const inputCls =
    "rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink-2 focus:border-line-strong focus:outline-none";

  const upsert = (role: string, hours: number, person = "") =>
    create.mutate(
      { projectId, person_name: person, role_key: role, allocation_pct: 1, hours_per_week: hours },
      { onError: (e) => toast.error((e as Error).message || "Couldn't save") },
    );

  const addSlot = () => {
    const hrs = parseFloat(newHours);
    if (!newRole || !hrs || hrs <= 0) {
      toast.error("Pick a role and hours/week");
      return;
    }
    upsert(newRole, hrs);
    setNewRole("");
    setNewHours("");
  };

  const available = ROLE_OPTIONS.filter((r) => !slots.some((s) => s.role_key === r.key));

  return (
    <div className="rounded-lg border border-line bg-canvas/60 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
        <Users className="h-3.5 w-3.5" /> Resource types needed
      </div>
      {isLoading ? (
        <div className="text-[11px] text-ink-subtle">Loading…</div>
      ) : (
        <>
          {slots.length === 0 && (
            <div className="mb-2 text-[11px] italic text-ink-subtle">
              No roles yet — add the resource types this project needs.
            </div>
          )}
          <div className="space-y-1.5">
            {slots.map((s) => (
              <div key={s.role_key + "|" + s.person_name} className="flex items-center gap-2">
                <span className="w-32 text-xs font-medium text-ink-2">
                  {ROLE_LABEL[s.role_key] ?? s.role_key}
                </span>
                {s.person_name ? (
                  <span className="w-28 truncate text-[10px] text-ink-subtle">{s.person_name}</span>
                ) : (
                  <span className="w-28 text-[10px] italic text-ink-subtle">role type</span>
                )}
                <input
                  type="number"
                  min="0"
                  className={cn(inputCls, "w-20")}
                  defaultValue={s.hours_per_week ?? ""}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (v > 0 && v !== s.hours_per_week) upsert(s.role_key, v, s.person_name);
                  }}
                />
                <span className="text-[10px] text-ink-subtle">h/wk</span>
                <button
                  onClick={() => del.mutate({ projectId, person_name: s.person_name, role_key: s.role_key })}
                  className="text-ink-subtle transition-colors hover:text-red-500"
                  title="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2 border-t border-line pt-2">
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className={inputCls}
            >
              <option value="">Add role…</option>
              {available.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
            <input
              type="number"
              min="0"
              placeholder="hrs/wk"
              value={newHours}
              onChange={(e) => setNewHours(e.target.value)}
              className={cn(inputCls, "w-24")}
            />
            <button
              onClick={addSlot}
              disabled={create.isPending || !newRole}
              className="inline-flex items-center gap-1 rounded-md bg-slate-800 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-slate-700 disabled:opacity-50"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        </>
      )}
    </div>
  );
}
